import { z } from 'zod';

import {
  RED_LINE_SESSION_STATES,
  RED_LINE_SIGNAL_REACTIONS,
  RED_LINE_MESSAGE_EMPHASIS,
} from './red-line';

export const REALTIME_PROTOCOL_VERSION = 1 as const;
export const REALTIME_SUBPROTOCOL = 'deffy-direct.v1';
export const REALTIME_TICKET_SUBPROTOCOL_PREFIX = 'ticket.';
export const MAX_REALTIME_FRAME_BYTES = 16 * 1024;
export const REALTIME_HEARTBEAT_INTERVAL_MS = 15_000;
export const REALTIME_STALE_AFTER_MS = 45_000;
export const REALTIME_TYPING_TTL_MS = 5_000;
export const REALTIME_EVENT_RETENTION = 500;
export const REALTIME_MAX_REPLAY_EVENTS = 100;

export const RealtimeRoleSchema = z.enum(['visitor', 'admin']);

export const RealtimeMessageSchema = z.object({
  publicId: z.string().min(8).max(80),
  clientMessageId: z.string().uuid().nullable(),
  sender: z.enum(['visitor', 'admin', 'system']),
  bodyMarkdown: z.string().max(10_000),
  type: z.enum(['text', 'signal']),
  state: z.enum(['transmitted', 'arrived', 'observed', 'failed', 'queued_offline']),
  createdAt: z.string().datetime(),
});

const readyEvent = z.object({
  type: z.literal('connection.ready'),
  payload: z.object({
    connectionId: z.string().min(8).max(80),
    role: RealtimeRoleSchema,
    heartbeatIntervalMs: z.number().int().positive(),
    latestSequence: z.number().int().nonnegative(),
    replayedEvents: z.number().int().nonnegative(),
  }),
});

const messageCreatedEvent = z.object({
  type: z.literal('message.created'),
  payload: z.object({ message: RealtimeMessageSchema }),
});

const messageStateChangedEvent = z.object({
  type: z.literal('message.state_changed'),
  payload: z.object({
    messagePublicId: z.string().min(8).max(80),
    state: z.enum(['transmitted', 'arrived', 'observed', 'failed', 'queued_offline']),
  }),
});

const typingStartedEvent = z.object({
  type: z.literal('typing.started'),
  payload: z.object({
    actor: RealtimeRoleSchema,
    expiresAt: z.string().datetime(),
  }),
});

const typingStoppedEvent = z.object({
  type: z.literal('typing.stopped'),
  payload: z.object({ actor: RealtimeRoleSchema }),
});

const presenceChangedEvent = z.object({
  type: z.literal('presence.changed'),
  payload: z.object({
    preset: z.enum(['very_botherable', 'building', 'slow_replies', 'sleeping']),
    label: z.string().max(80),
    subtitle: z.string().max(160).nullable(),
    privacyMode: z.boolean(),
  }),
});

const conversationPausedEvent = z.object({
  type: z.literal('conversation.paused'),
  payload: z.object({
    reason: z.string().max(160).nullable(),
    expectedReturn: z.string().datetime().nullable(),
  }),
});

const conversationResumedEvent = z.object({
  type: z.literal('conversation.resumed'),
  payload: z.object({ resumedAt: z.string().datetime() }),
});

const signalCreatedEvent = z.object({
  type: z.literal('signal.created'),
  payload: z.object({
    signal: z.enum(['seen', 'hold_on', 'running_out_of_time', 'patience_running_out']),
    message: RealtimeMessageSchema,
  }),
});

const attachmentReadyEvent = z.object({
  type: z.literal('attachment.ready'),
  payload: z.object({
    attachmentPublicId: z.string().min(8).max(80),
    messagePublicId: z.string().min(8).max(80),
  }),
});

const requestAcceptedEvent = z.object({
  type: z.literal('request.accepted'),
  payload: z.object({
    requestPublicId: z.string().min(8).max(80),
    conversationPublicId: z.string().min(8).max(80),
  }),
});

const requestRejectedEvent = z.object({
  type: z.literal('request.rejected'),
  payload: z.object({
    requestPublicId: z.string().min(8).max(80),
    reasonLabel: z.string().max(120),
  }),
});

const notificationPreferenceChangedEvent = z.object({
  type: z.literal('notification.preference_changed'),
  payload: z.object({ enabled: z.boolean() }),
});

const heartbeatPongEvent = z.object({
  type: z.literal('heartbeat.pong'),
  payload: z.object({ serverTimestamp: z.string().datetime() }),
});

const errorEvent = z.object({
  type: z.literal('error'),
  payload: z.object({
    code: z.string().min(1).max(80),
    message: z.string().min(1).max(240),
    retryable: z.boolean(),
  }),
});

const redLineOpenedEvent = z.object({
  type: z.literal('red_line.opened'),
  payload: z.object({
    conversationPublicId: z.string().min(8).max(80),
    sessionState: z.enum(RED_LINE_SESSION_STATES),
  }),
});

const redLineStateChangedEvent = z.object({
  type: z.literal('red_line.state_changed'),
  payload: z.object({
    previousState: z.enum(RED_LINE_SESSION_STATES),
    newState: z.enum(RED_LINE_SESSION_STATES),
    reason: z.string().max(160).nullable(),
  }),
});

const redLineSignalEvent = z.object({
  type: z.literal('red_line.signal'),
  payload: z.object({
    reaction: z.enum(RED_LINE_SIGNAL_REACTIONS),
    actor: z.enum(['visitor', 'admin']),
  }),
});

const redLinePriorityPingEvent = z.object({
  type: z.literal('red_line.priority_ping'),
  payload: z.object({
    actor: z.literal('visitor'),
    remaining: z.number().int().nonnegative(),
  }),
});

const redLineTypingEvent = z.object({
  type: z.literal('red_line.typing'),
  payload: z.object({
    actor: z.enum(['visitor', 'admin']),
    active: z.boolean(),
  }),
});

const redLinePresenceEvent = z.object({
  type: z.literal('red_line.presence'),
  payload: z.object({
    visitorPresent: z.boolean(),
    adminPresent: z.boolean(),
  }),
});

const redLineEndedEvent = z.object({
  type: z.literal('red_line.ended'),
  payload: z.object({
    reason: z.string().max(160).nullable(),
    endedBy: z.enum(['visitor', 'admin', 'system']),
  }),
});

const redLineConvertedEvent = z.object({
  type: z.literal('red_line.converted'),
  payload: z.object({
    convertedAt: z.string().datetime(),
    convertedBy: z.enum(['visitor', 'admin', 'system']),
  }),
});

const redLineMessageCreatedEvent = z.object({
  type: z.literal('red_line.message_created'),
  payload: z.object({
    message: RealtimeMessageSchema,
    emphasis: z.enum(RED_LINE_MESSAGE_EMPHASIS),
    contextChip: z.string().max(80).nullable(),
  }),
});

const signalPortalConfigChangedEvent = z.object({
  type: z.literal('signal_portal.config_changed'),
  payload: z.object({
    projectId: z.string().min(2).max(48),
    configVersion: z.number().int().positive(),
  }),
});

const signalPortalStatusChangedEvent = z.object({
  type: z.literal('signal_portal.status_changed'),
  payload: z.object({
    publicStatus: z.enum(['online', 'custom', 'offline']),
    customText: z.string().max(120).nullable(),
    customEmoji: z.string().max(20).nullable(),
    customTooltip: z.string().max(200).nullable(),
    customPostedAt: z.string().datetime().nullable(),
  }),
});

const conversationSeenEvent = z.object({
  type: z.literal('conversation.seen'),
  payload: z.object({
    messagePublicIds: z.array(z.string().min(8).max(80)).max(100),
    seenBy: z.enum(['visitor', 'admin']),
    manuallyMarked: z.boolean(),
  }),
});

const conversationTypingEvent = z.object({
  type: z.literal('conversation.typing'),
  payload: z.object({
    actor: z.enum(['visitor', 'admin']),
    active: z.boolean(),
  }),
});

const uploadPermissionsChangedEvent = z.object({
  type: z.literal('conversation.upload_permissions_changed'),
  payload: z.object({
    categories: z.array(z.string().max(40)).max(20),
  }),
});

const attachmentScanStartedEvent = z.object({
  type: z.literal('attachment.scan_started'),
  payload: z.object({
    attachmentPublicId: z.string().min(8).max(80),
  }),
});

const attachmentScanProgressEvent = z.object({
  type: z.literal('attachment.scan_progress'),
  payload: z.object({
    attachmentPublicId: z.string().min(8).max(80),
    progress: z.number().int().min(0).max(100),
  }),
});

const attachmentQuarantinedEvent = z.object({
  type: z.literal('attachment.quarantined'),
  payload: z.object({
    attachmentPublicId: z.string().min(8).max(80),
  }),
});

const attachmentFailedEvent = z.object({
  type: z.literal('attachment.failed'),
  payload: z.object({
    attachmentPublicId: z.string().min(8).max(80),
  }),
});

const linkPreviewReadyEvent = z.object({
  type: z.literal('link.preview_ready'),
  payload: z.object({
    messagePublicId: z.string().min(8).max(80),
    title: z.string().max(300),
    description: z.string().max(500),
    domain: z.string().max(200),
    url: z.string().max(2048),
  }),
});

const linkAnalysisReadyEvent = z.object({
  type: z.literal('link.analysis_ready'),
  payload: z.object({
    fetchJobId: z.string().min(8).max(80),
    messagePublicId: z.string().min(8).max(80).nullable(),
  }),
});

const conversationRetentionChangedEvent = z.object({
  type: z.literal('conversation.retention_changed'),
  payload: z.object({
    retentionMode: z.enum(['standard', 'recoverable', 'expired', 'archived']),
    recoveryExpiresAt: z.string().datetime().nullable(),
  }),
});

const eventEnvelopeFields = {
  version: z.literal(REALTIME_PROTOCOL_VERSION),
  eventId: z.string().min(8).max(80),
  conversationId: z.string().min(8).max(80),
  sequence: z.number().int().nonnegative(),
  timestamp: z.string().datetime(),
} as const;

export const RealtimeEventSchema = z.discriminatedUnion('type', [
  readyEvent.extend(eventEnvelopeFields),
  messageCreatedEvent.extend(eventEnvelopeFields),
  messageStateChangedEvent.extend(eventEnvelopeFields),
  typingStartedEvent.extend(eventEnvelopeFields),
  typingStoppedEvent.extend(eventEnvelopeFields),
  presenceChangedEvent.extend(eventEnvelopeFields),
  conversationPausedEvent.extend(eventEnvelopeFields),
  conversationResumedEvent.extend(eventEnvelopeFields),
  signalCreatedEvent.extend(eventEnvelopeFields),
  attachmentReadyEvent.extend(eventEnvelopeFields),
  requestAcceptedEvent.extend(eventEnvelopeFields),
  requestRejectedEvent.extend(eventEnvelopeFields),
  notificationPreferenceChangedEvent.extend(eventEnvelopeFields),
  heartbeatPongEvent.extend(eventEnvelopeFields),
  errorEvent.extend(eventEnvelopeFields),
  redLineOpenedEvent.extend(eventEnvelopeFields),
  redLineStateChangedEvent.extend(eventEnvelopeFields),
  redLineSignalEvent.extend(eventEnvelopeFields),
  redLinePriorityPingEvent.extend(eventEnvelopeFields),
  redLineTypingEvent.extend(eventEnvelopeFields),
  redLinePresenceEvent.extend(eventEnvelopeFields),
  redLineEndedEvent.extend(eventEnvelopeFields),
  redLineConvertedEvent.extend(eventEnvelopeFields),
  redLineMessageCreatedEvent.extend(eventEnvelopeFields),
  signalPortalConfigChangedEvent.extend(eventEnvelopeFields),
  signalPortalStatusChangedEvent.extend(eventEnvelopeFields),
  conversationSeenEvent.extend(eventEnvelopeFields),
  conversationTypingEvent.extend(eventEnvelopeFields),
  uploadPermissionsChangedEvent.extend(eventEnvelopeFields),
  attachmentScanStartedEvent.extend(eventEnvelopeFields),
  attachmentScanProgressEvent.extend(eventEnvelopeFields),
  attachmentQuarantinedEvent.extend(eventEnvelopeFields),
  attachmentFailedEvent.extend(eventEnvelopeFields),
  linkPreviewReadyEvent.extend(eventEnvelopeFields),
  linkAnalysisReadyEvent.extend(eventEnvelopeFields),
  conversationRetentionChangedEvent.extend(eventEnvelopeFields),
]);

export const RealtimePublishInputSchema = z.discriminatedUnion('type', [
  messageCreatedEvent,
  messageStateChangedEvent,
  presenceChangedEvent,
  conversationPausedEvent,
  conversationResumedEvent,
  signalCreatedEvent,
  attachmentReadyEvent,
  requestAcceptedEvent,
  requestRejectedEvent,
  notificationPreferenceChangedEvent,
  redLineOpenedEvent,
  redLineStateChangedEvent,
  redLineSignalEvent,
  redLinePriorityPingEvent,
  redLineTypingEvent,
  redLinePresenceEvent,
  redLineEndedEvent,
  redLineConvertedEvent,
  redLineMessageCreatedEvent,
  signalPortalConfigChangedEvent,
  signalPortalStatusChangedEvent,
  conversationSeenEvent,
  conversationTypingEvent,
  uploadPermissionsChangedEvent,
  attachmentScanStartedEvent,
  attachmentScanProgressEvent,
  attachmentQuarantinedEvent,
  attachmentFailedEvent,
  linkPreviewReadyEvent,
  linkAnalysisReadyEvent,
  conversationRetentionChangedEvent,
]);

export const RealtimeClientFrameSchema = z.discriminatedUnion('type', [
  z.object({
    version: z.literal(REALTIME_PROTOCOL_VERSION),
    type: z.literal('heartbeat.ping'),
    clientTimestamp: z.string().datetime(),
  }),
  z.object({
    version: z.literal(REALTIME_PROTOCOL_VERSION),
    type: z.literal('typing.set'),
    active: z.boolean(),
  }),
  z.object({
    version: z.literal(REALTIME_PROTOCOL_VERSION),
    type: z.literal('cursor.ack'),
    sequence: z.number().int().nonnegative(),
  }),
]);

export const RealtimeTicketResponseSchema = z.object({
  ticket: z.string().min(32).max(2_048),
  protocol: z.literal(REALTIME_SUBPROTOCOL),
  expiresAt: z.string().datetime(),
});

export type RealtimeRole = z.infer<typeof RealtimeRoleSchema>;
export type RealtimeEvent = z.infer<typeof RealtimeEventSchema>;
export type RealtimePublishInput = z.infer<typeof RealtimePublishInputSchema>;
export type RealtimeClientFrame = z.infer<typeof RealtimeClientFrameSchema>;
export type RealtimeTicketResponse = z.infer<typeof RealtimeTicketResponseSchema>;
export type RealtimeTransportState = 'idle' | 'connecting' | 'realtime' | 'fallback';
